<template>
  <div class="fi-cripto-page container-lg py-3 py-md-4 px-3">
    <!-- Cabecera -->
    <header class="fi-hero card border-0 shadow-sm mb-4">
      <div class="card-body p-3 p-md-4">
        <div class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
          <div>
            <h1 class="h3 mb-2 text-body">
              <i class="bi bi-coin me-2 text-primary" aria-hidden="true"></i>
              FI y Cripto
            </h1>
            <p class="mb-0 text-muted small fi-hero-desc">
              Vista de solo lectura del mismo spreadsheet que <strong>Evolución</strong>:
              cartera de fondos indexados y posiciones en cripto agregadas por moneda.
            </p>
          </div>
          <div class="d-flex flex-wrap gap-2 align-self-stretch align-self-md-center w-100 w-md-auto">
            <a
              v-if="sheetUrl"
              :href="sheetUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="btn btn-outline-secondary btn-sm flex-grow-1 flex-md-grow-0"
            >
              <i class="bi bi-box-arrow-up-right me-1" aria-hidden="true"></i>
              <span class="d-none d-sm-inline">Ver en </span>Sheets
            </a>
            <button
              type="button"
              class="btn btn-primary btn-sm flex-grow-1 flex-md-grow-0"
              :disabled="cargando"
              @click="cargarActivo"
            >
              <span
                v-if="cargando"
                class="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              ></span>
              <i v-else class="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
              {{ cargando ? 'Actualizando…' : 'Actualizar' }}
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Pestañas accesibles -->
    <div class="card border-0 shadow-sm mb-3">
      <div class="card-header bg-white border-bottom py-0 px-2 px-md-3">
        <ul
          class="nav nav-tabs fi-tabs border-0"
          role="tablist"
          aria-label="Secciones de cartera"
        >
          <li class="nav-item flex-fill" role="presentation">
            <button
              id="tab-fi"
              class="nav-link w-100 text-center"
              type="button"
              role="tab"
              :class="{ active: tab === 'fi' }"
              :aria-selected="tab === 'fi'"
              aria-controls="panel-fi"
              @click="cambiarTab('fi')"
            >
              <i class="bi bi-bank2 me-1" aria-hidden="true"></i>
              <span class="d-none d-sm-inline">Cartera </span>FI
            </button>
          </li>
          <li class="nav-item flex-fill" role="presentation">
            <button
              id="tab-cripto"
              class="nav-link w-100 text-center"
              type="button"
              role="tab"
              :class="{ active: tab === 'cripto' }"
              :aria-selected="tab === 'cripto'"
              aria-controls="panel-cripto"
              @click="cambiarTab('cripto')"
            >
              <i class="bi bi-currency-bitcoin me-1" aria-hidden="true"></i>
              Cripto
            </button>
          </li>
        </ul>
      </div>

      <div class="card-body p-3 p-md-4">
        <div v-if="errorActivo" class="alert alert-danger mb-0" role="alert">
          <i class="bi bi-exclamation-triangle me-2" aria-hidden="true"></i>
          <strong>No se pudieron cargar los datos.</strong> {{ errorActivo }}
        </div>

        <div
          v-else-if="cargando && !tieneDatosVisibles"
          class="text-center text-muted py-5"
          role="status"
          aria-live="polite"
        >
          <div class="spinner-border text-primary mb-2" aria-hidden="true"></div>
          <div>Cargando datos…</div>
        </div>

        <!-- Panel FI -->
        <section
          v-show="tab === 'fi' && !errorActivo"
          id="panel-fi"
          role="tabpanel"
          aria-labelledby="tab-fi"
          tabindex="0"
        >
          <div class="mb-3">
            <h2 class="h5 mb-1">Cartera de inversión</h2>
            <p class="small text-muted mb-0">
              Todas las posiciones en fondos indexados (incl. JITKey) — pestaña
              <span class="text-body fw-semibold">7. Cartera inversión</span>.
            </p>
          </div>

          <template v-if="posicionesFi.length">
            <!-- KPIs FI -->
            <div class="row g-3 mb-4">
              <div class="col-6 col-lg-3">
                <div class="card border-0 shadow-sm h-100 kpi-card kpi-total">
                  <div class="card-body">
                    <div class="kpi-label">Valor total</div>
                    <div class="kpi-value">{{ totalesFi?.importeTotal || formatoEuros(importeTotalFiNum) }}</div>
                    <div class="kpi-sub">{{ posicionesFi.length }} fondos</div>
                  </div>
                </div>
              </div>
              <div class="col-6 col-lg-3">
                <div class="card border-0 shadow-sm h-100 kpi-card">
                  <div class="card-body">
                    <div class="kpi-label">Aportación propia</div>
                    <div class="kpi-value">{{ totalesFi?.importePropio || '—' }}</div>
                    <div class="kpi-sub">capital invertido</div>
                  </div>
                </div>
              </div>
              <div class="col-6 col-lg-3">
                <div class="card border-0 shadow-sm h-100 kpi-card kpi-plus">
                  <div class="card-body">
                    <div class="kpi-label">Plusvalía latente</div>
                    <div class="kpi-value">
                      {{ totalesFi?.plusvaliaNum != null ? formatoEuros(totalesFi.plusvaliaNum) : '—' }}
                    </div>
                    <div class="kpi-sub">total − propio</div>
                  </div>
                </div>
              </div>
              <div class="col-6 col-lg-3">
                <div class="card border-0 shadow-sm h-100 kpi-card kpi-rent">
                  <div class="card-body">
                    <div class="kpi-label">% rentabilidad</div>
                    <div class="kpi-value">{{ totalesFi?.pctRentabilidad || '—' }}</div>
                    <div class="kpi-sub">sobre aportación</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tabla limpia de fondos -->
            <div class="card border-0 shadow-sm">
              <div class="card-header bg-white border-bottom py-2">
                <h3 class="h6 mb-0">
                  <i class="bi bi-list-ul me-1 text-primary" aria-hidden="true"></i>
                  Posiciones por fondo
                </h3>
              </div>
              <div class="card-body p-0">
                <div class="table-responsive fi-table-scroll" tabindex="0">
                  <table class="table table-sm table-hover mb-0 cartera-table">
                    <caption class="visually-hidden">
                      Listado de fondos de la cartera con importe, peso real, peso objetivo y desviación.
                    </caption>
                    <thead class="table-light">
                      <tr>
                        <th scope="col">Titular</th>
                        <th scope="col">Gestora</th>
                        <th scope="col">Fondo</th>
                        <th scope="col" class="d-none d-md-table-cell">ISIN</th>
                        <th scope="col" class="d-none d-lg-table-cell">Geografía</th>
                        <th scope="col" class="text-end d-none d-md-table-cell">Particip.</th>
                        <th scope="col" class="text-end">Importe</th>
                        <th scope="col" class="text-end">% real</th>
                        <th scope="col" class="text-end d-none d-lg-table-cell">% obj.</th>
                        <th scope="col" class="text-end d-none d-lg-table-cell">Δ %</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(p, i) in posicionesFi" :key="'fi-' + i">
                        <td>
                          <span class="badge titular-badge" :class="claseTitular(p.titular)">{{ p.titular || '—' }}</span>
                        </td>
                        <td>
                          <span class="badge gestora-badge">{{ p.gestora || '—' }}</span>
                        </td>
                        <td>
                          <div class="fw-semibold lh-sm">{{ p.nombre }}</div>
                          <div class="small text-muted d-md-none">
                            {{ p.geografia }}<span v-if="p.geografia"> · </span>{{ p.isin }}
                          </div>
                        </td>
                        <td class="font-monospace small text-muted d-none d-md-table-cell">{{ p.isin || '—' }}</td>
                        <td class="d-none d-lg-table-cell text-muted">{{ p.geografia || '—' }}</td>
                        <td class="text-end font-monospace text-muted d-none d-md-table-cell">{{ p.participaciones || '—' }}</td>
                        <td class="text-end fw-semibold">{{ p.importe || '—' }}</td>
                        <td class="text-end">{{ p.pctReal || '—' }}</td>
                        <td class="text-end text-muted d-none d-lg-table-cell">{{ p.pctObjetivo || '—' }}</td>
                        <td
                          class="text-end d-none d-lg-table-cell"
                          :class="claseDelta(p.deltaPctNum)"
                        >{{ p.deltaPct || '—' }}</td>
                      </tr>
                    </tbody>
                    <tfoot v-if="totalesFi" class="table-group-divider">
                      <tr class="table-light fw-bold">
                        <th scope="row" colspan="3">Total fondos</th>
                        <td class="d-none d-md-table-cell"></td>
                        <td class="d-none d-lg-table-cell"></td>
                        <td class="text-end d-none d-md-table-cell text-muted small">
                          <span v-if="totalesFi.comisionMedia">Com. {{ totalesFi.comisionMedia }}</span>
                        </td>
                        <td class="text-end">{{ totalesFi.importeTotal || formatoEuros(importeTotalFiNum) }}</td>
                        <td class="text-end">100%</td>
                        <td class="text-end d-none d-lg-table-cell"></td>
                        <td class="text-end d-none d-lg-table-cell"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          </template>
          <p v-else-if="!cargando" class="text-muted small mb-0">No hay datos de cartera FI.</p>
        </section>

        <!-- Panel Cripto -->
        <section
          v-show="tab === 'cripto' && !errorActivo"
          id="panel-cripto"
          role="tabpanel"
          aria-labelledby="tab-cripto"
          tabindex="0"
        >
          <div class="mb-3">
            <h2 class="h5 mb-1">Cartera cripto agregada</h2>
            <p class="small text-muted mb-0">
              Pestaña <span class="text-body fw-semibold">7.2 Ag. Cripto</span> (sumas por moneda).
            </p>
          </div>

          <template v-if="filasCripto.length">
            <!-- KPI total -->
            <div class="row g-3 mb-4">
              <div class="col-12 col-sm-6 col-lg-4">
                <div class="card border-0 bg-dark text-white h-100 shadow-sm cripto-kpi">
                  <div class="card-body">
                    <div class="small text-white-50 text-uppercase fw-semibold mb-1">
                      Valor total cartera
                    </div>
                    <div class="fs-4 fw-bold" aria-live="polite">
                      {{ formatoEuros(totalImporteCripto) }}
                    </div>
                    <div class="small text-white-50 mt-1">
                      {{ activosCriptoParsed.length }} activo(s) · datos en euros del sheet
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-12 col-sm-6 col-lg-8">
                <div class="card border-0 bg-light h-100 shadow-sm">
                  <div class="card-body d-flex align-items-center">
                    <p class="small text-muted mb-0">
                      <i class="bi bi-info-circle me-1 text-primary" aria-hidden="true"></i>
                      La gráfica agrupa los activos con menor peso en <strong>Otros</strong> para que se lean bien los principales.
                      Pasa el cursor o el dedo sobre cada segmento para ver importe y porcentaje.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="row g-4 align-items-stretch">
              <div class="col-12 col-lg-5">
                <div class="card border-0 shadow-sm h-100 cripto-chart-card">
                  <div class="card-header bg-white border-bottom py-2">
                    <h3 class="h6 mb-0">
                      <i class="bi bi-pie-chart-fill me-1 text-primary" aria-hidden="true"></i>
                      Distribución por importe
                    </h3>
                  </div>
                  <div class="card-body">
                    <div
                      class="cripto-chart-container mx-auto"
                      :style="{ maxWidth: chartMaxPx + 'px', height: chartMaxPx + 'px' }"
                    >
                      <Doughnut
                        v-if="chartCriptoData.labels.length"
                        :data="chartCriptoData"
                        :options="chartCriptoOptions"
                        aria-label="Gráfico de distribución del valor por moneda"
                      />
                      <p v-else class="text-muted small text-center mb-0 py-4">
                        No hay importes numéricos suficientes para mostrar la gráfica.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-12 col-lg-7">
                <div class="card border-0 shadow-sm h-100">
                  <div class="card-header bg-white border-bottom py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h3 class="h6 mb-0">
                      <i class="bi bi-list-ul me-1 text-primary" aria-hidden="true"></i>
                      Detalle por moneda
                    </h3>
                  </div>
                  <div class="card-body p-0">
                    <div class="table-responsive cripto-table-wrap">
                      <table class="table table-sm table-hover mb-0 cripto-table">
                        <caption class="visually-hidden">
                          Posiciones por moneda: cantidad, valor en euros y porcentaje sobre el total.
                        </caption>
                        <thead>
                          <tr>
                            <th scope="col">{{ encabezadosCripto[0] }}</th>
                            <th scope="col" class="text-end">{{ encabezadosCripto[1] }}</th>
                            <th scope="col" class="text-end">{{ encabezadosCripto[2] }}</th>
                            <th scope="col" class="text-end">{{ encabezadosCripto[3] }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(fila, i) in filasCriptoNormalizadas" :key="'cr-' + i">
                            <td class="fw-semibold">{{ fila[0] }}</td>
                            <td class="text-end font-monospace text-muted">{{ fila[1] }}</td>
                            <td class="text-end">{{ fila[2] }}</td>
                            <td class="text-end">{{ fila[3] }}</td>
                          </tr>
                        </tbody>
                        <tfoot class="table-group-divider">
                          <tr class="table-light fw-bold">
                            <th scope="row">Total</th>
                            <td class="text-end text-muted small">—</td>
                            <td class="text-end">{{ formatoEuros(totalImporteCripto) }}</td>
                            <td class="text-end">100%</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <p v-else-if="!cargando" class="text-muted small mb-0">No hay datos de cripto.</p>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { Doughnut } from 'vue-chartjs';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import axios from '../config/axios';

ChartJS.register(ArcElement, Tooltip, Legend);

const ENC_CRIPTO_DEF = ['Moneda', 'Cantidad', 'Importe (€)', '% cartera'];
const TOP_N_CHART = 6;

/** Convierte "23.040,13 €" o "1.234,5" a número (locale es-ES). */
function parseEuroEs(str) {
  if (str == null) return 0;
  const s = String(str).replace(/€/g, '').replace(/\s/g, '').trim();
  if (!s) return 0;
  const normalized = s.includes(',')
    ? s.replace(/\./g, '').replace(',', '.')
    : s.replace(/\./g, '');
  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : 0;
}

const CRIPTO_COLORS = [
  '#f7931a',
  '#627eea',
  '#9945ff',
  '#14f195',
  '#2775ca',
  '#e6007a',
  '#345d9d',
  '#6c757d'
];

export default {
  name: 'FiCripto',
  components: { Doughnut },
  setup() {
    const tab = ref('fi');
    const datos = ref({ fi: null, cripto: null });
    const errores = ref({ fi: '', cripto: '' });
    const cargando = ref(false);
    const sheetUrl = ref('');
    const windowWide = ref(typeof window !== 'undefined' ? window.innerWidth >= 992 : true);

    const posicionesFi = computed(() => datos.value.fi?.posiciones || []);
    const totalesFi = computed(() => datos.value.fi?.totales || null);
    const importeTotalFiNum = computed(() =>
      posicionesFi.value.reduce((s, p) => s + (p.importeNum || 0), 0)
    );

    const filasCripto = computed(() => datos.value.cripto?.filas || []);
    const encabezadosCripto = computed(
      () => datos.value.cripto?.encabezados || ENC_CRIPTO_DEF
    );

    const errorActivo = computed(() => errores.value[tab.value]);

    const tieneDatosVisibles = computed(() =>
      tab.value === 'fi' ? posicionesFi.value.length > 0 : filasCripto.value.length > 0
    );

    const filasCriptoNormalizadas = computed(() =>
      filasCripto.value.map((r) => {
        const a = [...r];
        while (a.length < 4) a.push('');
        return a.slice(0, 4);
      })
    );

    const activosCriptoParsed = computed(() =>
      filasCriptoNormalizadas.value.map((fila) => ({
        moneda: String(fila[0] ?? '').trim(),
        cantidadStr: String(fila[1] ?? '').trim(),
        eurosStr: String(fila[2] ?? '').trim(),
        pctStr: String(fila[3] ?? '').trim(),
        euros: parseEuroEs(fila[2])
      })).filter((a) => a.moneda)
    );

    const totalImporteCripto = computed(() =>
      activosCriptoParsed.value.reduce((s, a) => s + a.euros, 0)
    );

    const chartSlices = computed(() => {
      const list = [...activosCriptoParsed.value]
        .filter((a) => a.euros > 0.005)
        .sort((a, b) => b.euros - a.euros);
      const total = totalImporteCripto.value;
      if (!list.length || total <= 0) return { labels: [], values: [], colors: [] };

      const top = list.slice(0, TOP_N_CHART);
      const rest = list.slice(TOP_N_CHART);
      const restSum = rest.reduce((s, a) => s + a.euros, 0);

      const labels = top.map((a) => a.moneda);
      const values = top.map((a) => a.euros);
      const colors = top.map((_, i) => CRIPTO_COLORS[i % CRIPTO_COLORS.length]);

      if (restSum > 0.01) {
        labels.push('Otros');
        values.push(restSum);
        colors.push(CRIPTO_COLORS[CRIPTO_COLORS.length - 1]);
      }

      return { labels, values, colors };
    });

    const chartCriptoData = computed(() => {
      const { labels, values, colors } = chartSlices.value;
      return {
        labels,
        datasets: [{
          data: values,
          backgroundColor: colors.map((c) => (c.length === 7 ? `${c}cc` : c)),
          borderColor: '#fff',
          borderWidth: 2,
          hoverOffset: 8
        }]
      };
    });

    const chartMaxPx = computed(() => (windowWide.value ? 300 : 240));

    const chartCriptoOptions = computed(() => {
      const total = totalImporteCripto.value;
      return {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '56%',
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              boxWidth: 12,
              padding: 10,
              font: { size: windowWide.value ? 12 : 10 },
              usePointStyle: true
            }
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const label = ctx.label ? `${ctx.label}: ` : '';
                const v = ctx.parsed;
                const pct = total > 0 ? ((v / total) * 100).toFixed(2) : '0';
                return `${label}${formatoEuros(v)} (${pct}%)`;
              }
            }
          }
        }
      };
    });

    function formatoEuros(n) {
      if (n == null || !Number.isFinite(n)) return '—';
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: n >= 100 ? 0 : 2
      }).format(n);
    }

    function claseDelta(n) {
      if (n == null || !Number.isFinite(n) || Math.abs(n) < 0.01) return 'text-muted';
      return n >= 0 ? 'text-success' : 'text-danger';
    }

    function claseTitular(titular) {
      const t = String(titular || '').trim().toLowerCase();
      if (t === 'jitkey') return 'titular-jitkey';
      if (t === 'vicente') return 'titular-vicente';
      return '';
    }

    async function cargar(t) {
      cargando.value = true;
      errores.value[t] = '';
      try {
        const endpoint = t === 'fi' ? '/api/evolucion/cartera-fi' : '/api/evolucion/cartera-cripto';
        const { data } = await axios.get(endpoint);
        datos.value[t] = data;
        if (data.sheetUrl) sheetUrl.value = data.sheetUrl;
      } catch (e) {
        errores.value[t] = e?.response?.data?.error || e.message || 'Error al cargar';
      } finally {
        cargando.value = false;
      }
    }

    function cargarActivo() {
      cargar(tab.value);
    }

    function cambiarTab(t) {
      tab.value = t;
      const d = datos.value[t];
      if (t === 'cripto' && d && !Array.isArray(d.encabezados)) {
        datos.value.cripto = null;
      }
      if (!datos.value[t]) cargar(t);
    }

    function onResize() {
      windowWide.value = typeof window !== 'undefined' ? window.innerWidth >= 992 : true;
    }

    onMounted(() => {
      cargar('fi');
      window.addEventListener('resize', onResize, { passive: true });
    });

    onUnmounted(() => {
      window.removeEventListener('resize', onResize);
    });

    return {
      tab,
      cargando,
      sheetUrl,
      errorActivo,
      tieneDatosVisibles,
      posicionesFi,
      totalesFi,
      importeTotalFiNum,
      filasCripto,
      encabezadosCripto,
      filasCriptoNormalizadas,
      claseDelta,
      claseTitular,
      cargarActivo,
      cambiarTab,
      totalImporteCripto,
      activosCriptoParsed,
      chartCriptoData,
      chartCriptoOptions,
      chartMaxPx,
      formatoEuros
    };
  }
};
</script>

<style scoped>
.fi-cripto-page {
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}

.fi-hero-desc {
  max-width: 42rem;
}

.fi-tabs .nav-link {
  border: none;
  border-bottom: 3px solid transparent;
  color: #6c757d;
  font-weight: 500;
  padding: 0.75rem 1rem;
}

.fi-tabs .nav-link:hover {
  color: #0d6efd;
  border-bottom-color: rgba(13, 110, 253, 0.25);
}

.fi-tabs .nav-link.active {
  color: #0d6efd;
  border-bottom-color: #0d6efd;
  background: transparent;
}

.kpi-card .card-body {
  padding: 1rem 1.1rem;
}

.kpi-card .kpi-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.kpi-card .kpi-value {
  font-size: 1.2rem;
  font-weight: 700;
  color: #212529;
  line-height: 1.2;
  word-break: break-word;
}

.kpi-card .kpi-sub {
  font-size: 0.7rem;
  color: #6c757d;
  margin-top: 0.2rem;
}

.kpi-card.kpi-total {
  border-left: 4px solid #0d6efd !important;
}

.kpi-card.kpi-plus {
  border-left: 4px solid #198754 !important;
}

.kpi-card.kpi-plus .kpi-value {
  color: #146c43;
}

.kpi-card.kpi-rent {
  border-left: 4px solid #6f42c1 !important;
}

.kpi-card.kpi-rent .kpi-value {
  color: #6f42c1;
}

.gestora-badge {
  background: #e7f1ff;
  color: #0d6efd;
  font-weight: 600;
  letter-spacing: 0.02em;
  font-size: 0.7rem;
  padding: 0.3rem 0.55rem;
}

.titular-badge {
  font-weight: 600;
  letter-spacing: 0.02em;
  font-size: 0.7rem;
  padding: 0.3rem 0.55rem;
  background: #e9ecef;
  color: #495057;
}

.titular-badge.titular-jitkey {
  background: #fff3cd;
  color: #856404;
}

.titular-badge.titular-vicente {
  background: #d1e7dd;
  color: #0f5132;
}

.fi-table-scroll {
  max-height: min(70vh, 560px);
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

.fi-table-scroll:focus-visible {
  outline: 2px solid #0d6efd;
  outline-offset: 2px;
}

.cartera-table {
  font-size: 0.8125rem;
  margin-bottom: 0;
}

.cartera-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
  box-shadow: 0 1px 0 #dee2e6;
}

.cartera-table tbody td {
  vertical-align: middle;
}

.cartera-table tfoot th,
.cartera-table tfoot td {
  border-top: 2px solid #dee2e6;
  padding-top: 0.65rem;
  padding-bottom: 0.65rem;
}

.cripto-kpi .card-body {
  padding: 1rem 1.25rem;
}

.cripto-chart-card .card-body {
  padding: 1rem;
}

.cripto-chart-container {
  position: relative;
  width: 100%;
  min-height: 200px;
}

.cripto-table-wrap {
  max-height: min(55vh, 420px);
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

.cripto-table {
  font-size: 0.875rem;
  margin-bottom: 0;
}

.cripto-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #212529;
  color: #fff;
  font-weight: 600;
  font-size: 0.75rem;
  border: none;
  padding: 0.65rem 0.75rem;
  white-space: nowrap;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.08);
}

.cripto-table tbody td {
  padding: 0.55rem 0.75rem;
  vertical-align: middle;
}

.cripto-table tbody tr:hover {
  background-color: #f8f9fa;
}

.cripto-table tfoot th,
.cripto-table tfoot td {
  padding: 0.65rem 0.75rem;
  border-top: 2px solid #dee2e6;
}

@media (max-width: 575.98px) {
  .cartera-table {
    font-size: 0.75rem;
  }

  .cripto-table {
    font-size: 0.8125rem;
  }
}
</style>
